import { useParams, useLocation } from 'wouter';
import { useEffect, useState } from 'react';
import { parseWhatsAppTicketRef, getWhatsAppTicketRedirectUrl } from '@/utils/whatsapp-ticket-ref';
import { useTranslation } from 'react-i18next';

/**
 * Resolver for /ticket/:ticketRef when ref is env-encoded (prd_<uuid> or stg_<uuid>).
 * Redirects to the correct domain + /ticket/view/<ticketId>. Non-encoded refs show invalid link.
 */
export default function WhatsAppTicketRefResolver() {
  const { ticketRef } = useParams<{ ticketRef: string }>();
  const [, setLocation] = useLocation();
  const { t } = useTranslation('common');
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    if (!ticketRef) {
      setResolved(true);
      return;
    }
    const parsed = parseWhatsAppTicketRef(ticketRef);
    if (parsed) {
      const url = getWhatsAppTicketRedirectUrl(parsed);
      const sameOrigin = url.startsWith(window.location.origin);
      if (sameOrigin) {
        setLocation(`/ticket/view/${parsed.ticketId}`);
      } else {
        window.location.href = url;
      }
      return;
    }
    setResolved(true);
  }, [ticketRef, setLocation]);

  if (!resolved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">{t('loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md px-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {t('whatsappTicketLink.invalidTitle', 'Invalid ticket link')}
        </h1>
        <p className="text-gray-600">
          {t('whatsappTicketLink.invalidMessage', 'This ticket link is invalid or expired. Please use the link from your latest WhatsApp message.')}
        </p>
      </div>
    </div>
  );
}
